import { ContactForm } from '../types'
import { sendFreeEmail } from './freeEmailService'

// EmailJS配置 - 临时使用默认值，需要时再配置
const EMAILJS_CONFIG = {
  serviceId: 'service_not_configured',
  templateId: 'template_not_configured',
  publicKey: 'key_not_configured'
}

// 动态加载EmailJS
const loadEmailJS = async () => {
  if (typeof window !== 'undefined' && !(window as any).emailjs) {
    const emailjs = await import('@emailjs/browser')
    return emailjs
  }
  return (window as any).emailjs
}

// 发送联系表单邮件
export const sendContactEmail = async (formData: ContactForm): Promise<boolean> => {
  try {
    // 检查是否配置了EmailJS
    if (EMAILJS_CONFIG.serviceId === 'service_not_configured') {
      console.log('EmailJS not configured, skipping...')
      return false
    }

    const emailjs = await loadEmailJS()
    
    // 准备模板参数
    const templateParams = {
      to_email: 'rfidlib@libmj.com', // 您的邮箱
      from_name: formData.name,
      from_email: formData.email,
      company: formData.company || 'Not specified',
      phone: formData.phone || 'Not provided',
      message: formData.message,
      reply_to: formData.email,
      // 添加时间戳
      timestamp: new Date().toLocaleString('zh-CN', {
        timeZone: 'Asia/Shanghai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }

    console.log('Sending email with params:', templateParams)

    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templateId,
      templateParams,
      EMAILJS_CONFIG.publicKey
    )

    console.log('Email sent successfully:', response.status, response.text)
    return true
  } catch (error) {
    console.error('Email sending failed:', error)
    return false
  }
}

// 备用方案：使用Formspree
export const sendContactEmailFormspree = async (formData: ContactForm): Promise<boolean> => {
  try {
    const formspreeEndpoint = null // 暂时不配置
    
    if (!formspreeEndpoint) {
      console.log('Formspree not configured, skipping...')
      return false
    }

    const response = await fetch(formspreeEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: formData.name,
        email: formData.email,
        company: formData.company,
        phone: formData.phone,
        message: formData.message,
        _replyto: formData.email,
        _subject: `New Contact Form Submission from ${formData.name}`,
        _template: 'table'
      }),
    })

    return response.ok
  } catch (error) {
    console.error('Formspree sending failed:', error)
    return false
  }
}

// 主要的邮件发送函数，带有多重备用方案
export const sendEmail = async (formData: ContactForm, language: string = 'en'): Promise<boolean> => {
  console.log('📧 Starting email sending process...')
  
  // 首先尝试免费邮件服务（推荐）
  try {
    console.log('🔄 Trying free email services...')
    const freeEmailSuccess = await sendFreeEmail(formData, language)
    if (freeEmailSuccess) {
      console.log('✅ Email sent successfully via free service')
      return true
    }
  } catch (error) {
    console.warn('Free email services failed, trying other options...', error)
  }

  // 然后尝试EmailJS（如果配置了）
  try {
    const emailjsSuccess = await sendContactEmail(formData)
    if (emailjsSuccess) {
      console.log('✅ Email sent successfully via EmailJS')
      return true
    }
  } catch (error) {
    console.warn('EmailJS failed, trying backup...', error)
  }

  // 如果以上都失败，使用内置存储方案
  try {
    console.log('🔄 Using built-in storage as fallback...')
    return await sendContactEmailBuiltIn(formData)
  } catch (error) {
    console.error('All email services failed:', error)
    return false
  }
}

// Web3Forms备用方案
export const sendContactEmailWeb3Forms = async (formData: ContactForm): Promise<boolean> => {
  try {
    const web3formsKey = null // 暂时不配置
    
    if (!web3formsKey) {
      // 如果没有配置Web3Forms，使用内置的简单邮件发送
      console.log('Web3Forms not configured, using built-in solution...')
      return await sendContactEmailBuiltIn(formData)
    }

    const response = await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        access_key: web3formsKey,
        name: formData.name,
        email: formData.email,
        company: formData.company,
        phone: formData.phone,
        message: formData.message,
        subject: `New Contact from ${formData.name} - MiJin Technology Website`,
        from_name: 'MiJin Technology Website',
        to: 'rfidlib@libmj.com'
      }),
    })

    const result = await response.json()
    return result.success
  } catch (error) {
    console.error('Web3Forms failed:', error)
    return false
  }
}

// 内置简单邮件发送（最后的备用方案）
export const sendContactEmailBuiltIn = async (formData: ContactForm): Promise<boolean> => {
  try {
    // 保存到本地存储作为备份
    const submissions = JSON.parse(localStorage.getItem('contact_submissions') || '[]')
    const newSubmission = {
      ...formData,
      timestamp: new Date().toISOString(),
      timestampFormatted: new Date().toLocaleString('zh-CN', { 
        timeZone: 'Asia/Shanghai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      id: Date.now().toString()
    }
    
    submissions.push(newSubmission)
    localStorage.setItem('contact_submissions', JSON.stringify(submissions))

    // 在控制台显示详细信息，方便查看
    console.group('📧 新的联系表单提交')
    console.log('姓名:', formData.name)
    console.log('公司:', formData.company || '未提供')
    console.log('邮箱:', formData.email)
    console.log('电话:', formData.phone || '未提供')
    console.log('消息:', formData.message)
    console.log('提交时间:', newSubmission.timestampFormatted)
    console.log('完整数据:', newSubmission)
    console.groupEnd()

    // 显示用户友好的信息
    console.log(`💾 表单已保存到本地存储 (总计 ${submissions.length} 条记录)`)
    console.log('📊 查看所有提交: localStorage.getItem("contact_submissions")')
    
    return true
  } catch (error) {
    console.error('Built-in email sending failed:', error)
    return false
  }
}
